import React, { useEffect, useRef, useState } from 'react'
import Analytics from 'utils/googleAnalytics'
import Dropzone from 'react-dropzone'
import { useCookies } from 'react-cookie'

import { toggleModal as toggleModalAction } from 'redux/active/actions'
import { useActions, useAppSelector } from 'redux/hooks'
import { setDeckStateAction } from 'redux/deck/actions'

import { MassEditParseResponse } from 'services/apiTypes/card.types'
import { CategoryType, EDH_FORMATS, generateDefaultCategory } from 'types/deck'
import { ROLES } from 'types/user'

import SaveService from 'services/save.service'
import RequestService from 'services/request.service'
import ToastService from 'services/toast.service'

import Icon from 'components/elements/Icon'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import PhatInput from 'components/formElements/PhatInput'
import PhatButton from 'components/formElements/PhatButton'
import Accordion from 'components/elements/Accordion'
import SimpleSpinner from 'components/elements/SimpleSpinner'

import { deckToString } from 'utils/DeckToString'
import { responseToDeckCards } from 'utils/ResponseToResults'

import { SynchronizeCategoriesResponseType } from 'services/apiTypes/deck.types'

import styles from './import.module.scss'

export const IMPORT_PLACEHOLDER =
  "Paste your deck (or Arena export) here......\n\n\tExamples...\n\n\t2 Sensei's Divining Top\n\t2x Sensei's Divining Top\n\nOptionally specify collector number:\n\n\t1 Mountain (DOM) 263\n\nYou can specify a group of categories:\n\n\t# Sideboard\n\t1 Counterspell\n\t1 Path to Exile"

type Props = Record<string, never>

/**
 * This component is giant and should be broken down into smaller pieces
 *
 * But for the sake of documentation, this component is that handles the mass deck editor.
 *
 * Currently this deals with opening the modal and operations dealing with creating/ parsing the text for the mass edit.
 * There is a lot of string manipulation going on here, so take a look at the methods of this class to get a good idea
 * of what's going here.
 */
const ImportOverlay: React.FC<Props> = () => {
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const categories = useAppSelector(state => state.deck.categories)
  const deckFormat = useAppSelector(state => state.deck.format)
  const deckId = useAppSelector(state => state.deck.id)

  const open = useAppSelector(state => state.active.modals.massEditModal)

  const [{ tbR: roles = [] }] = useCookies(['tbR'])

  const isModerator = roles.includes(ROLES.MOD)

  const [deckString, setDeckString] = useState(deckToString(Object.values(cardMap), categories))
  const [originalDeckString, setOriginalDeckString] = useState(deckToString(Object.values(cardMap), categories))
  const [externalUrl, setExternalUrl] = useState('')
  const [isConfirmingClose, setIsConfirmingClose] = useState(false)
  const [file, setFile] = useState<File>()

  const [loading, setLoading] = useState(false)
  const [syntaxErrors, setSyntaxErrors] = useState<string[]>([])
  const [cardErrors, setCardErrors] = useState<string[]>([])
  const [success, setSuccess] = useState(false)
  const [needsSave, setNeedsSave] = useState(false)

  const reloadListenerRef = useRef<(e: Event) => void>(() => null)

  const [setDeckState, toggleModal] = useActions(setDeckStateAction, toggleModalAction)

  const handleTextFieldChange = (value: string) => {
    setDeckString(value)

    if (!needsSave) setNeedsSave(true)
  }

  const synchronizeCategories = async (newCategories: Record<string, CategoryType>) => {
    if (!deckId) return // We're in sandbox, no need to sync with remote
    const data = await RequestService.patch<SynchronizeCategoriesResponseType>(
      `/api/decks/${deckId}/synchronizeCategories/`,
      { categories: Object.values(newCategories) },
    )
    const syncedMap = { ...newCategories }
    data.categories.forEach((category: CategoryType) => (syncedMap[category.name] = category))
    setDeckStateAction({ categories: syncedMap })
  }

  const handleSave = () => {
    const body = {
      parser: 'archidekt',
      current: originalDeckString,
      edit: deckString,
    }

    setLoading(true)

    return RequestService.post<MassEditParseResponse>('/api/cards/massDeckEdit/', body)
      .then(handleResponse)
      .finally(() => setLoading(false))
  }

  const handleImportFromUrl = () => {
    setLoading(true)

    return RequestService.get<MassEditParseResponse>(`/api/decks/importUrl/?url=${encodeURIComponent(externalUrl)}`)
      .then(handleResponse)
      .then(() => setExternalUrl(''))
      .finally(() => setLoading(false))
  }

  const handleResponse = async (data: MassEditParseResponse) => {
    setSyntaxErrors(data.syntaxErrors)
    setCardErrors(data.cardErrors)

    // Set any changed categories
    const massCategories = { ...categories }

    for (let key in data.categories) {
      if (!massCategories[key]) massCategories[key] = { ...generateDefaultCategory(), ...data.categories[key] }
      else if (key !== 'Maybeboard' && !(EDH_FORMATS.includes(deckFormat) && key === 'Commander'))
        massCategories[key] = { ...data.categories[key], id: massCategories[key].id }
    }

    setDeckState({ categories: massCategories })

    // Parse through the deck given the cards to remove by name and category and get their IDs
    let toRemoveIds: Array<string> = []

    for (const cardToRemove of data.toRemove) {
      toRemoveIds = toRemoveIds.concat(
        Object.values(cardMap)
          .filter(
            card =>
              card.name === cardToRemove.name &&
              `${cardToRemove.edition || ''}` === card.setCode &&
              ((!cardToRemove.categories.length && card.categories[0] === card.typeCategory) ||
                cardToRemove.categories[0] === card.categories[0]),
          )
          .map(card => card.id),
      )
    }

    Analytics.signalEvent('Saved mass edit form')

    const updatedCard = responseToDeckCards(data.toAdd)

    return SaveService.save(updatedCard, toRemoveIds)
      .then(() => synchronizeCategories(massCategories))
      .then(() => {
        if (!data.syntaxErrors.length && !data.cardErrors.length) setSuccess(true)

        setNeedsSave(false)
      })
  }

  useEffect(() => {
    if (!open) return

    reloadListenerRef.current = (e: Event) => {
      e.preventDefault()

      // @ts-ignore - Returning this way so as pop a message on close tabs
      // This prevents chrome from closing a tab without checking first
      return (e.returnValue = 'Are you sure you want to close?')
    }

    if (!needsSave) window.removeEventListener('beforeunload', reloadListenerRef.current)
    else window.addEventListener('beforeunload', reloadListenerRef.current)

    return () => {
      window.removeEventListener('beforeunload', reloadListenerRef.current)
    }
  }, [open, needsSave])

  // Ensure that the originalDeckString represents the current deck state
  useEffect(() => {
    if (!open) return

    const defaultString = deckToString(Object.values(cardMap), categories)

    setOriginalDeckString(defaultString)
    setDeckString(defaultString)

    setCardErrors([])
    setSyntaxErrors([])
    setSuccess(false)
  }, [open])

  // Ensure that the originalDeckString represents the current deck state
  useEffect(() => {
    setOriginalDeckString(deckToString(Object.values(cardMap), categories))
  }, [cardMap])

  const handleModalClose = () => {
    if (!isConfirmingClose && needsSave) return setIsConfirmingClose(true)

    setIsConfirmingClose(false)
    toggleModal('massEditModal', false)
  }

  const handleFileDrop = (accepted: Array<any>, _: Array<any>) => (accepted.length ? setFile(accepted[0]) : null)
  const handleReadFile = () => {
    if (!file) return

    Analytics.signalEvent('Importing via file')

    const reader = new FileReader()

    reader.onabort = () => console.log('file reading was aborted')
    reader.onerror = () => console.log('file reading has failed')
    reader.onload = () => {
      const fileContent = `${reader?.result || ''}`

      // prettier-ignore
      if (fileContent === deckString) return ToastService.create('File matches current deck exactly, skipping upload', 'Deck Import', 'warning')

      setDeckString(fileContent)

      ToastService.create('Upload overwrote import form, confirm below', 'Deck Import', 'success')
    }

    file && reader.readAsText(file)
  }

  const submitButton = (
    <PhatButton disabled={deckString === originalDeckString || loading} onClick={handleSave} color="green">
      {!loading && 'Save Changes'}
      {loading && (
        <>
          <SimpleSpinner size="xSmall" /> Saving ...
        </>
      )}
    </PhatButton>
  )

  return (
    <GlobalOverlayStack
      noWeirdClose
      open={open}
      onClose={handleModalClose}
      className={styles.container}
      mobileControlsClassName={styles.mobileControls}
      mobileControls={submitButton}>
      <h3 className={styles.header}>Import Cards</h3>

      <ConfirmDeleteModal
        open={isConfirmingClose}
        header="Leave without saving"
        message="Are you sure you want to close? You have unconfirmed changes to your import field."
        buttonText="Close it anyway"
        onClose={() => setIsConfirmingClose(false)}
        onConfirm={handleModalClose}
      />

      <div className={styles.accordionGroup}>
        <div>
          <Accordion label="Formatting Examples" containerClass={styles.accordionContent}>
            <p>2 Sensei's Divining Top</p>
            <p>2x Sol Ring</p>
            <p>1x Path to Exile (cmm) [Removal] ^To Remove,#FF0000^</p>

            <p className={styles.topSpace}># Sideboard</p>
            <p>1 Wrath of God</p>
            <p>2 Island</p>
            <p>1 Forest</p>
          </Accordion>
        </div>

        <div>
          <Accordion label="Upload exported Archidekt file" containerClass={styles.alternateForm}>
            <Dropzone
              onDrop={handleFileDrop}
              // prettier-ignore
              onDropRejected={() => ToastService.create('Unable to process file. Check your file size, is it less than 1MB?', 'Deck Import', 'error')}
              accept=".txt"
              multiple={false}
              maxSize={1000000}
              className={styles.fileDropzone}>
              {file ? (
                <p>
                  File name: <i>{file.name}</i>
                </p>
              ) : (
                <p>
                  Select or drop an Archidekt exported file here <b>(Max 1MB)</b>
                </p>
              )}
            </Dropzone>
            <PhatButton disabled={!file} onClick={handleReadFile} color="green">
              Upload file to form
              <Icon name="arrow right" />
            </PhatButton>
          </Accordion>
        </div>

        {isModerator && (
          <div>
            <Accordion label="Import from external URL" containerClass={styles.alternateForm}>
              <PhatInput
                className={styles.externalUrlField}
                value={externalUrl}
                onChange={setExternalUrl}
                placeholder="https://www.moxfield.com/decks/4BWENxYcrUCDY1EVsJ2vfA"
              />

              <PhatButton color="green" onClick={handleImportFromUrl} disabled={loading || !externalUrl}>
                Import from URL <Icon name="arrow right" />
              </PhatButton>
            </Accordion>
          </div>
        )}
      </div>

      <textarea
        className={styles.input}
        name="deckString"
        value={deckString}
        onChange={e => handleTextFieldChange(e.target.value)}
        disabled={loading}
        placeholder={IMPORT_PLACEHOLDER}
      />

      <div className={styles.desktopControls}>{submitButton}</div>

      {!!cardErrors.length && (
        <div className={styles.errors}>
          <h4>Card errors</h4>

          {cardErrors.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </div>
      )}

      {!!syntaxErrors.length && (
        <div className={styles.errors}>
          <h4>Syntax errors</h4>

          {syntaxErrors.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </div>
      )}

      {success && <div className={styles.success}>All cards listed have been found!</div>}
    </GlobalOverlayStack>
  )
}

export default ImportOverlay
